// Lageroverføring Service - Bruker eksisterende StcTransOrder endepunkter
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface WarehouseTransferRequest {
  itemNo: string;
  fromLocationName: string;
  toLocationName: string;
  quantity: number;
  fromWarehouse: string;
  toWarehouse: string;
  description1?: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  itemType?: number;
}

export interface WarehouseTransferLine {
  lineID: number;
  itemNo: string;
  fromLocationName: string;
  toLocationName: string;
  quantity: number;
  fromWarehouse: string;
  toWarehouse: string;
  description1: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  transDt: Date;
  lineStatus: number;
  statusText: string;
}

export interface WarehouseTransferSession {
  batchID: string;
  operatorID: string;
  description1: string;
  orderStatus: number;
  statusText: string;
  warehouse: string;
  orderDate: Date;
  transactionCount: number;
  lines: WarehouseTransferLine[];
}

export interface ItemValidationResult {
  valid: boolean;
  itemNo: string;
  description1?: string;
  itemType?: number;
  errorMessage?: string;
}

export interface LocationValidationResult {
  valid: boolean;
  locationName: string;
  warehouseID?: string;
  errorMessage?: string;
}

// Nye interfaces for lagerbeholdning
export interface ItemLocationInfo {
  itemNo: string;
  description: string;
  locationName: string;
  warehouseID: string;
  availableQuantity: number;
  totalQuantity: number;
  reservedQuantity: number;
  binCodeFixed: boolean;
  binCodePrimary: boolean;
  lastMovementDate?: Date;
  maxQuantity?: number;
  availableSpace?: number;
  itemType?: number;
  serialNo?: string;
}

export interface LocationItemInfo {
  locationName: string;
  warehouseID: string;
  itemNo: string;
  description: string;
  availableQuantity: number;
  totalQuantity: number;
  reservedQuantity: number;
  binCodeFixed: boolean;
  binCodePrimary: boolean;
  lastMovementDate?: Date;
  maxQuantity?: number;
  availableSpace?: number;
  itemType?: number;
  serialNo?: string;
}

export interface WarehouseTransferFormData {
  scannedCode: string;
  quantity: string;
  isScanning: boolean;
  validationMessage: string;
  validationType: 'success' | 'error' | '';
}

class WarehouseTransferService {
  private baseUrl = API_BASE_URL;

  // Få aktiv overføringssession for operatør (top 1)
  async getActiveTransferSession(operatorId: string): Promise<WarehouseTransferSession | null> {
    try {
      // Hent alle transfer sessions for operatøren (OrderType 2 = warehouse transfer, Status 20 = Under arbeid)
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/operator/${operatorId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch transfer sessions');
      }
      const sessions = await response.json();
      
      // Finn aktiv transfer session (status 20 og OrderType 2)
      const activeSessions = sessions.filter((s: any) => 
        s.orderType === 2 && s.orderStatus === 20
      );
      
      if (activeSessions.length === 0) {
        return null;
      }

      // Ta den nyeste
      const activeSession = activeSessions.sort((a: any, b: any) => 
        new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
      )[0];

      // Hent detaljer med transakcsjoner
      const detailResponse = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${activeSession.batchID}/details`);
      if (!detailResponse.ok) {
        throw new Error('Failed to fetch session details');
      }
      const details = await detailResponse.json();

      return this.mapToTransferSession(details);
    } catch (error) {
      console.error('Error getting active transfer session:', error);
      return null;
    }
  }

  // Opprett ny overføringssession
  async createTransferSession(operatorId: string, warehouse: string): Promise<WarehouseTransferSession> {
    try {
      const now = new Date();
      const batchID = `${now.toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14)} ${operatorId}`;
      
      const request = {
        batchID,
        operatorID: operatorId,
        orderDate: now,
        description1: `${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO')} ${operatorId}`,
        orderStatus: 20, // Under arbeid
        profileID: `20000477${operatorId}`, // Standard profil
        orderType: 2, // Warehouse transfer (ikke 1 som er counting)
        warehouse,
        lineTypeOrigin: 0 // Standard
        // StatID genereres automatisk av backend (erstatter INSERT trigger)
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to create transfer session');
      }
      
      // Return session med tomme linjer
      const responseData = await response.json();
      return {
        batchID: responseData.batchID,
        operatorID: responseData.operatorID,
        description1: responseData.description1,
        orderStatus: responseData.orderStatus,
        statusText: responseData.statusText,
        warehouse: responseData.warehouse,
        orderDate: new Date(responseData.orderDate),
        transactionCount: 0,
        lines: []
      };
    } catch (error) {
      console.error('Error creating transfer session:', error);
      throw new Error('Kunne ikke opprette overføringssession');
    }
  }

  // Valider varenummer
  async validateItem(itemNo: string): Promise<ItemValidationResult> {
    try {
      console.log('🔍 [validateItem] Validating item:', itemNo);
      const startTime = performance.now();

      // Try barcode search first
      const barcodeResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/barcode/current?barcode=${encodeURIComponent(itemNo)}`);

      const barcodeEndTime = performance.now();
      console.log(`⏱️ [validateItem] Barcode search took ${Math.round(barcodeEndTime - startTime)}ms, status: ${barcodeResponse.status}`);

      if (barcodeResponse.ok) {
        const barcodeData = await barcodeResponse.json();
        if (barcodeData && barcodeData.length > 0) {
          // Only accept exact barcode match — partial LIKE matches should fall through
          // to item search which has serial/batch priority ordering
          const exactBarcodeMatch = barcodeData.find((i: any) =>
            i.itemBarCode && i.itemBarCode.toLowerCase() === itemNo.toLowerCase()
          );
          if (exactBarcodeMatch) {
            console.log('✅ [validateItem] Item found via exact barcode:', exactBarcodeMatch.itemNo);
            return {
              valid: true,
              itemNo: exactBarcodeMatch.itemNo,
              description1: exactBarcodeMatch.description1,
              itemType: exactBarcodeMatch.itemType
            };
          }
          console.log('🔍 [validateItem] Barcode LIKE match found but no exact match, continuing to item search...');
        }
      }

      // Barcode didn't match - try direct item number search
      console.log('🔍 [validateItem] Barcode not found, trying direct item number search...');
      const itemSearchStart = performance.now();
      const itemResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/search/current?searchTerm=${encodeURIComponent(itemNo)}`);

      const itemSearchEnd = performance.now();
      console.log(`⏱️ [validateItem] Item search took ${Math.round(itemSearchEnd - itemSearchStart)}ms, status: ${itemResponse.status}`);

      if (!itemResponse.ok) {
        return {
          valid: false,
          itemNo,
          errorMessage: `Vare "${itemNo}" ble ikke funnet`
        };
      }

      const itemData = await itemResponse.json();
      if (itemData && itemData.length > 0) {
        // Find exact match if exists, otherwise take first result
        const exactMatch = itemData.find((i: any) => i.itemNo.toLowerCase() === itemNo.toLowerCase());
        const item = exactMatch || itemData[0];
        console.log('✅ [validateItem] Item found via item search:', item.itemNo);
        return {
          valid: true,
          itemNo: item.itemNo,
          description1: item.description1,
          itemType: item.itemType
        };
      }

      console.log('❌ [validateItem] Item not found');
      return {
        valid: false,
        itemNo,
        errorMessage: `Vare "${itemNo}" ble ikke funnet`
      };
    } catch (error) {
      console.error('❌ [validateItem] Error validating item:', error);
      return {
        valid: false,
        itemNo,
        errorMessage: 'Feil ved validering av vare'
      };
    }
  }

  // Valider lokasjon - OPTIMALISERT til å bruke dedikert exact-match endpoint
  async validateLocation(companyNo: string, locationName: string): Promise<LocationValidationResult> {
    try {
      if (!companyNo) {
        throw new Error('Company number is required');
      }

      // Use dedicated exact-match endpoint for fast validation
      const response = await AuthService.authenticatedGet(
        `${this.baseUrl}/stc-bins/exact/${companyNo}/${encodeURIComponent(locationName)}`
      );

      if (response.status === 404) {
        // Location not found
        return {
          valid: false,
          locationName,
          errorMessage: `Lokasjon "${locationName}" ble ikke funnet`
        };
      }

      if (!response.ok) {
        throw new Error('Failed to validate location');
      }

      const bin = await response.json();

      return {
        valid: true,
        locationName: bin.binCode,
        warehouseID: bin.warehouseID
      };
    } catch (error) {
      console.error('Error validating location:', error);
      return {
        valid: false,
        locationName,
        errorMessage: 'Feil ved validering av lokasjon'
      };
    }
  }

  // NYE METODER - Hent lokasjoner for en vare (med lagerbeholdning)
  async getLocationsForItem(itemNo: string): Promise<ItemLocationInfo[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/data-inspection/itembins/item/${encodeURIComponent(itemNo)}`);
      if (!response.ok) {
        throw new Error('Failed to get locations for item');
      }
      
      const itemBins = await response.json();

      // Aggreger per BinCode+WarehouseID for å unngå duplikater (batch-varer har flere ItemBins-rader per lokasjon)
      const locationMap = new Map<string, ItemLocationInfo>();
      for (const bin of itemBins) {
        const key = `${bin.binCode}|${bin.warehouseID}`;
        const existing = locationMap.get(key);
        if (existing) {
          existing.availableQuantity += (bin.quantityAvailable || 0);
          existing.totalQuantity += (bin.quantity || 0);
          existing.reservedQuantity += (bin.quantityReserved || 0);
          if (bin.lastMovementDate) {
            const newDate = new Date(bin.lastMovementDate);
            if (!existing.lastMovementDate || newDate > existing.lastMovementDate) {
              existing.lastMovementDate = newDate;
            }
          }
        } else {
          locationMap.set(key, {
            itemNo: bin.itemNo,
            description: bin.itemDescription || '',
            locationName: bin.binCode,
            warehouseID: bin.warehouseID,
            availableQuantity: bin.quantityAvailable || 0,
            totalQuantity: bin.quantity || 0,
            reservedQuantity: bin.quantityReserved || 0,
            binCodeFixed: bin.binCodeFixed || false,
            binCodePrimary: bin.binCodePrimary || false,
            lastMovementDate: bin.lastMovementDate ? new Date(bin.lastMovementDate) : undefined,
            maxQuantity: bin.maxQuantity,
            availableSpace: bin.availableSpace,
            itemType: bin.itemType,
            serialNo: bin.serialNo
          });
        }
      }
      return Array.from(locationMap.values()).filter(location => location.availableQuantity > 0);
    } catch (error) {
      console.error('Error getting locations for item:', error);
      return [];
    }
  }

  // NYE METODER - Hent varer på en lokasjon (med lagerbeholdning)
  async getItemsAtLocation(locationName: string): Promise<LocationItemInfo[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/data-inspection/itembins/bin/${encodeURIComponent(locationName)}`);
      if (!response.ok) {
        throw new Error('Failed to get items at location');
      }
      
      const itemBins = await response.json();
      
      return itemBins.map((bin: any): LocationItemInfo => ({
        locationName: bin.binCode,
        warehouseID: bin.warehouseID,
        itemNo: bin.itemNo,
        description: bin.itemDescription || '',
        availableQuantity: bin.quantityAvailable || 0,
        totalQuantity: bin.quantity || 0,
        reservedQuantity: bin.quantityReserved || 0,
        binCodeFixed: bin.binCodeFixed || false,
        binCodePrimary: bin.binCodePrimary || false,
        lastMovementDate: bin.lastMovementDate ? new Date(bin.lastMovementDate) : undefined,
        maxQuantity: bin.maxQuantity,
        availableSpace: bin.availableSpace,
        itemType: bin.itemType,
        serialNo: bin.serialNo
      })).filter((item: LocationItemInfo) => item.availableQuantity > 0); // Kun varer med tilgjengelig antall
    } catch (error) {
      console.error('Error getting items at location:', error);
      return [];
    }
  }

  // Legg til overføringslinje
  async addTransferLine(batchId: string, transferData: WarehouseTransferRequest): Promise<WarehouseTransferLine> {
    try {
      // Hent operatorID fra batchID (format: "YYYYMMDDHHMMSS OperatorID")
      const operatorID = batchId.split(' ').slice(1).join(' ');
      
      const request = {
        batchID: batchId,
        lineType: 2, // Transfer line type (ikke 1 som er counting)
        orderNo: 0,
        lineNo: 0,
        locationName: transferData.fromLocationName, // Fra-lokasjon
        quantity: transferData.quantity,
        transDt: new Date(),
        toLocationName: transferData.toLocationName, // Til-lokasjon
        warehouse: transferData.fromWarehouse,
        toWarehouse: transferData.toWarehouse,
        operatorID: operatorID,
        profileID: `20000477${operatorID}`, // Samme som ved opprettelse av session
        exportedYesNo: false,
        lineStatus: 20, // Under arbeid (ikke 40 som for telling)
        itemNo: transferData.itemNo,
        itemUnitCode: '',
        itemBarCode: null,
        description1: transferData.description1 || '',
        itemBatchNo: transferData.itemBatchNo || '',
        itemBatchExpDate: transferData.itemBatchExpDate,
        itemSerialNo: transferData.itemSerialNo || '',
        erpLinkName: '',
        shipmentNo: '1', // Standard shipmentNo
        quantityOrigin: 0,
        itemUnitTxt: '',
        lineTypeOrigin: 0, // Standard
        price: 0,
        currency: '',
        reasonCode: 0,
        transType: 0, // Standard transfer type (ikke 4 som er telling)
        sscc: '',
        quantityCorrected: null,
        freeField1: null,
        freeField2: null,
        freeField3: null,
        appFreeField1: null,
        appFreeField2: null,
        quantityGross: 0,
        transitPackagingID: 0,
        stcBoxNo: 0,
        grShipmentNo: 0,
        externalSystemID: null,
        externalSystemStatus: null,
        itemType: transferData.itemType || 0,
        orderNoERP: null,
        lineNoERP: 0
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/transactions`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to add transfer line');
      }
      
      const responseData = await response.json();
      return this.mapToTransferLine(responseData);
    } catch (error) {
      console.error('Error adding transfer line:', error);
      throw new Error('Kunne ikke legge til overføringslinje');
    }
  }

  // Hent overføringslinjer for session
  async getTransferLines(batchId: string): Promise<WarehouseTransferLine[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${batchId}/transactions`);
      if (!response.ok) {
        throw new Error('Failed to fetch transfer lines');
      }
      const transactions = await response.json();
      
      return transactions
        .filter((t: any) => t.transType === 0 && t.lineType === 2) // Transfer linjer
        .map((t: any) => this.mapToTransferLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime());
    } catch (error) {
      console.error('Error getting transfer lines:', error);
      return [];
    }
  }

  // Fullfør overføringssession
  async completeTransferSession(batchId: string): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/complete`);
      if (!response.ok) {
        throw new Error('Failed to complete transfer session');
      }
      return true;
    } catch (error) {
      console.error('Error completing transfer session:', error);
      return false;
    }
  }

  // Oppdater overføringslinje
  async updateTransferLine(batchId: string, lineId: number, quantity: number): Promise<WarehouseTransferLine> {
    try {
      const request = {
        quantity: quantity
      };

      const response = await AuthService.authenticatedPut(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to update transfer line');
      }
      
      const responseData = await response.json();
      return this.mapToTransferLine(responseData);
    } catch (error) {
      console.error('Error updating transfer line:', error);
      throw new Error('Kunne ikke oppdatere overføringslinje');
    }
  }

  // Slett overføringslinje
  async deleteTransferLine(batchId: string, lineId: number): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedDelete(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to delete transfer line');
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting transfer line:', error);
      throw new Error('Kunne ikke slette overføringslinje');
    }
  }

  // Helper methods
  private mapToTransferSession(details: any): WarehouseTransferSession {
    return {
      batchID: details.header.batchID,
      operatorID: details.header.operatorID,
      description1: details.header.description1,
      orderStatus: details.header.orderStatus,
      statusText: details.header.statusText,
      warehouse: details.header.warehouse,
      orderDate: new Date(details.header.orderDate),
      transactionCount: details.totalTransactions,
      lines: details.transactions
        .filter((t: any) => t.transType === 0 && t.lineType === 2) // Transfer linjer
        .map((t: any) => this.mapToTransferLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime())
    };
  }

  private mapToTransferLine(transaction: any): WarehouseTransferLine {
    return {
      lineID: transaction.lineID,
      itemNo: transaction.itemNo,
      fromLocationName: transaction.locationName,
      toLocationName: transaction.toLocationName,
      quantity: transaction.quantity,
      fromWarehouse: transaction.warehouse,
      toWarehouse: transaction.toWarehouse,
      description1: transaction.description1,
      itemBatchNo: transaction.itemBatchNo,
      itemBatchExpDate: transaction.itemBatchExpDate ? new Date(String(transaction.itemBatchExpDate).substring(0, 10) + 'T12:00:00') : undefined,
      itemSerialNo: transaction.itemSerialNo,
      transDt: new Date(transaction.transDt),
      lineStatus: transaction.lineStatus,
      statusText: transaction.statusText || 'Under arbeid'
    };
  }
}

export const warehouseTransferService = new WarehouseTransferService();
